/**
 * Data-sources status (v1.5.0): shows which providers are active for each
 * capability (fundamentals, prices, filings) and surfaces anything that's
 * unavailable and why. Because the free sources need no keys, this normally
 * shows everything active out of the box.
 */

import React, { useEffect, useState } from "react";
import { apiClient } from "@/api/client";
import type { ProviderStatus } from "@/types";

const CAPABILITY_LABELS: Record<string, string> = {
  fundamentals: "Fundamentals",
  prices: "Prices",
  filings: "Filings",
};

export default function DataSourcesStatus(): React.ReactElement | null {
  const [status, setStatus] = useState<ProviderStatus | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get<ProviderStatus>("/providers/status")
      .then(({ data }) => {
        if (!cancelled) setStatus(data);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error || !status) return null;

  const capabilities = Object.entries(status.capabilities);
  const unavailable = capabilities.flatMap(([, c]) => c.unavailable);

  return (
    <div className="panel flex flex-wrap items-center gap-x-6 gap-y-2 px-5 py-3 text-xs">
      <span className="font-medium uppercase tracking-wide text-mist-400">Data sources</span>
      {capabilities.map(([key, c]) => (
        <span key={key} className="flex items-center gap-1.5">
          <span
            className={`h-1.5 w-1.5 rounded-full ${c.available ? "bg-signal-teal" : "bg-signal-rose"}`}
            aria-hidden
          />
          <span className="text-mist-300">{CAPABILITY_LABELS[key] ?? key}:</span>
          <span className="font-mono text-mist-100">{c.active_provider ?? "none"}</span>
        </span>
      ))}
      {unavailable.length > 0 && (
        <span className="text-mist-500">
          ({unavailable.map((u) => `${u.name}: ${u.reason}`).join(" · ")})
        </span>
      )}
    </div>
  );
}
